import { getBridgeSdk } from "@checkout-core/utils/BridgeSdk";
import {
    BackAlert,
    GetConfigValueRequestPayload,
    NavigationScreenLinkTypes,
    NavigationTypes,
} from "@swiggy-private/native-bridge";
import { clearAuthHeadersFromStorage } from "@checkout-core/helpers/Storage";
//constants
import { GIFTING_HOME_DEEPLINK } from "@checkout-core/constants/GiftingCart";
import { isEmpty } from "@swiggy-private/js-utils";
import { isToingFlow, isIxigoFlow } from "../helpers";
import { SHELL_APP_DEEPLINK_PREFIX } from "../constants";

const OOPS_SOMETHING_WENT_WRONG = "Oops Something went wrong!!";

const FOOD_PAGE_DEEPLINK = "swiggy://restaurantList";

interface NativeAlertArgs {
    message?: string;
    title?: string;
    buttonName?: string;
}

export const showNativeAlert = async ({
    message = OOPS_SOMETHING_WENT_WRONG,
    title = " ",
    buttonName = "Ok",
}: NativeAlertArgs = {}): Promise<void> => {
    getBridgeSdk()
        .alert({
            message,
            title,
            buttonName,
        })
        .catch(() => void 0);
};

export const showNativeHeader = async ({
    title = "",
    backURL,
    backAlert,
}: {
    title?: string;
    backURL: string;
    backAlert?: BackAlert;
}): Promise<void> => {
    getBridgeSdk()
        .showHeader({ show: true, title, backURL, backAlert })
        .catch(() => void 0);
};

export const hideNativeHeader = (): void => {
    getBridgeSdk()
        .showHeader({ show: false, title: "" })
        .catch(() => void 0);
};

export const closeWebview = (data?: {
    params: {
        [key: string]: string;
    };
}): void => {
    clearAuthHeadersFromStorage();

    getBridgeSdk()
        .close(data)
        .catch(() => void 0);
};

/**
 * Closes the Ixigo webview using Ixigo bridge's closeView method
 * This should be used when navigating to home or restaurant listing in Ixigo flow
 * @returns boolean - true if closeView was called successfully, false otherwise
 */
export const closeIxigoWebview = (): boolean => {
    if (typeof window === "undefined" || !window.ixigoBridge?.closeView) {
        return false;
    }

    try {
        window.ixigoBridge.closeView();
        return true;
    } catch (_) {
        return false;
    }
};

export const manageSwipe = async (disable: boolean, height?: number): Promise<void> => {
    try {
        getBridgeSdk().manageSwipeGesture({ disable, height });
    } catch (_) {
        void 0;
    }
};

export const getCheckoutWebDynamicConfigValues = async (payload: GetConfigValueRequestPayload) => {
    try {
        const response = await getBridgeSdk().getConfigValue(payload);
        const xpValues = response?.xpValues;
        const wpValues = response?.wpValues;

        return { xpValues, wpValues };
    } catch (_) {
        void 0;
    }
};

export const goToRestaurantListingPage = () => {
    // For Ixigo flow, close webview directly instead of navigating
    if (isIxigoFlow()) {
        closeIxigoWebview();
        return;
    }

    getBridgeSdk()
        .navigateToScreen({
            type: NavigationTypes.DEEPLINK,
            link: getNativeChannelDeepLink(FOOD_PAGE_DEEPLINK),
            params: {
                shouldCloseWebview: true,
            },
        })
        .catch(() => void 0);
    return;
};

export const clearCart = () => {
    getBridgeSdk()
        .clearLocalCart({
            tenant: {
                marketplaceBusinessline: "Regular",
                marketplaceCategory: "Food",
                marketplaceId: 1,
            },
        })
        .catch(() => void 0);
};

export const goToHome = (): void => {
    // For Ixigo flow, close webview directly instead of navigating
    if (isIxigoFlow()) {
        closeIxigoWebview();
        return;
    }

    getBridgeSdk()
        .navigateToScreen({
            type: NavigationTypes.SCREEN,
            link: NavigationScreenLinkTypes.HOME,
            params: {
                shouldCloseWebview: true,
            },
        })
        .catch(() => void 0);
};

export const clearDineoutLocalCart = (flowType?: string) => {
    getBridgeSdk()
        .dineoutClearLocalCart({ flowType })
        .catch(() => void 0);
};

export const goToGiftingHome = (): void => {
    getBridgeSdk()
        .navigateToScreen({
            type: NavigationTypes.DEEPLINK,
            link: GIFTING_HOME_DEEPLINK,
            params: {
                shouldCloseWebview: true,
            },
        })
        .catch(() => void 0);
};

export const getNativeChannelDeepLink = (deeplink: string) => {
    if (isEmpty(deeplink)) {
        return deeplink;
    }
    if (isToingFlow()) {
        return deeplink.replace(/swiggy:\/\//g, SHELL_APP_DEEPLINK_PREFIX.TOING_APP);
    }
    return deeplink;
};
